import { shouldRenderRunAction } from 'js/components/pipelines/cards/components/ExpandActions/utils/shouldRenderSourceAction';
import {
  ADD_DATA_SOURCE_REQUEST,
  ADD_SINK_REQUEST,
  GET_DATA_SOURCE_REQUEST,
  GET_SINK_REQUEST,
  PROBE_GET_TREE_REQUEST,
  UPDATE_SINK_INFO_REQUEST,
  UPDATE_SOURCE_INFO_REQUEST,
} from 'js/constants/actionTypes';
import { CONNECTION_CATEGORY_DATABASE } from 'js/constants/connectionCategories';
import { FORM_TYPE_CREATE_SPARK } from 'js/constants/formTypes';
import { isEmpty } from 'lodash';
import useFlowsAdapter from 'new/components/flows/adapter/useFlowsAdapter';
import createDatabaseService from 'new/components/flows/configure/Database/service/createDatabaseService';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';
import { useChangeDetection } from 'new/components/flows/hooks/useChangeDetection';
import createFlowService from 'new/components/flows/service/createFlowService';
import flowTypes from 'new/components/flows/types/flowTypes';
import { createResourceSetupService } from 'new/components/flows/types/spark/CreateSparkFlow/ResourceSetup/service/createResourceSetupService';
import useFlowParams from 'new/components/flows/useFlowParams';
import useFlowQueryParams from 'new/components/flows/useFlowQueryParams';
import useFlows from 'new/components/flows/useFlows';
import { useForm } from 'new/elements/Form/useForm';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { usePreviousPath } from 'new/hooks/usePreviousPath';
import useSchemas from 'new/state/dataSetSchemas/useSchemas';
import usePaging from 'new/state/paging/repository/usePaging';
import useProbe from 'new/state/probe/repository/useProbe';
import useRequests from 'new/state/request/repository/useRequests';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useSpark from '../../useSpark';
import { useSparkSaveConfirmation } from './useSparkSaveConfirmation';
import useAppDispatch from 'js/hooks/useAppDispatch';
import {
  assignFlowToProjectIfPresent,
  redirectToProjectIfNeeded,
} from 'new/utils/projectAssignment';
import { useSelector } from 'react-redux';
import { getCurrentProjectId } from 'js/reducers/app/selectors';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { ROUTE_PIPELINES } from 'js/constants/pathnames';
import { useHeaderMetaConfig } from 'new/components/flows/context/useHeaderMetaConfig';
import { withUiModeCode, withUiModeDefault } from 'new/utils/uiMode';

export default function useResourceSetup() {
  const dispatch = useAppDispatch();
  const currentProjectId = useSelector(getCurrentProjectId);
  const { name: metaName, description: metaDescription } = useHeaderMetaConfig();
  const { setLoaderMessages, overrideNextFunction, setConfirmButtonTitle } = useForm();

  // Save confirmation logic
  const {
    showSaveConfirmation,
    setShowSaveConfirmation,
    isSaving,
    setIsSaving,
    pendingSaveRef,
    currentRunWithIngestion,
    hasChanges,
    shouldShowSaveConfirmation,
    handleSaveConfirmation,
    onCloseSaveConfirmation,
    setShowLeaveConfirmation,
    overrideNextFunction: overrideFormNextFunction,
  } = useSparkSaveConfirmation();

  // Get change detection at component level
  const { hasChanges: currentHasChanges } = useChangeDetection(FORM_TYPE_CREATE_SPARK);
  const { showError } = useRequests();
  const previousPath = usePreviousPath();
  const {
    sourceConfig,
    selectedConnection,
    sourceConfigFields,
    sinkConfig,
    selectedBucket,
    selectedTable,
    createSource: requestSourceCreation,
    updateSource: requestSourceUpdate,
    createSink: requestSinkCreation,
    updateSink: requestSinkUpdate,
    connectionType,
    nexset,
    source,
    sink,
    isNewTable,
    credentialId,
    updateFormData,
    selectedCredential,
  } = useSpark();
  const { resourceType, id, isModal, onDismiss } = useFlowParams();
  const { getProbeData } = useProbe();
  const { getSinkConfigFields, getMetaInfoById } = usePaging();

  const probe = getProbeData(credentialId);

  const metaInfo = getMetaInfoById(selectedConnection);
  const connectionCategory = metaInfo?.connectionCategory;
  const sinkConfigFields = getSinkConfigFields(selectedConnection, flowTypes.spark);

  const {
    onBucketSelected,
    onSourceConfigChanged: updateSourceConfig,
    onSinkConfigChanged: updateSinkConfig,
    onTableSelected,
    validate,
    setupInitialFormData,
    cleanForm,
  } = useFlowsSetup(FORM_TYPE_CREATE_SPARK, flowTypes.spark);

  const [initialLoading, setInitialLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const currentOrg = useCurrentOrg();
  const { flowId } = useFlowQueryParams();
  const { requestResources } = useFlowsAdapter();
  const { onSinkMappingUpdated } = useFlows(updateFormData);
  const flowService = createFlowService();
  const databaseService = createDatabaseService();
  const { getNexsetSchemaById } = useSchemas();
  const service = createResourceSetupService();

  useEffect(() => {
    setLoaderMessages({
      [UPDATE_SOURCE_INFO_REQUEST]: 'Updating source info',
      [GET_SINK_REQUEST]: 'Loading destination',
      [GET_DATA_SOURCE_REQUEST]: 'Loading source',
      [PROBE_GET_TREE_REQUEST]: isEmpty(probe.data)
        ? `Connecting to ${metaInfo.displayName}`
        : null,
      [ADD_DATA_SOURCE_REQUEST]: 'Creating source',
      [UPDATE_SINK_INFO_REQUEST]: 'Updating destination info',
      [ADD_SINK_REQUEST]: 'Creating destination',
    });
  }, [probe.loading]);

  useEffect(() => {
    if (!sink && connectionType === 'SINK' && source) {
      setConfirmButtonTitle('Done');
      return;
    }
    if ((sink && connectionType === 'SINK') || (source && connectionType === 'SOURCE')) {
      setConfirmButtonTitle('Save');
    }
    return () => {
      setConfirmButtonTitle('Continue');
    };
  }, [sink, source, connectionType]);

  const setup = async () => {
    setInitialLoading(true);

    const requestParams = { resourceType, id, flowId };
    const resources = await requestResources(requestParams);

    if (!id && !flowId) {
      setInitialLoading(false);
      return;
    }

    /**
     * Only initialize the FormData if there are valid resources
     * Form is being initialized in the useFlowsSetup hook [onSelectConnection call]
     * It it is called again it will override the form data
     */
    setupInitialFormData(resources);
    setInitialLoading(false);
  };

  useEffect(() => {
    setup();
  }, [resourceType]);

  const buildSourceInfo = useCallback(() => {
    const { name, description, sourceName, sourceDescription, ...sourceConfigValues } =
      sourceConfig || {};

    let newSourceConfig = flowService.createDefaultSourceConfig({
      sourceConfig: sourceConfigValues,
      configFields: sourceConfigFields,
      selectedConnection,
      orgId: currentOrg.id,
    });

    if (connectionCategory === CONNECTION_CATEGORY_DATABASE) {
      newSourceConfig = databaseService.buildSourceConfig({
        sourceConfig: sourceConfigValues,
        configFields: sourceConfigFields,
      });
    }

    const sourceConfigWithMode = withUiModeDefault(newSourceConfig);

    return {
      ...(source?.id ? { id: source.id } : {}),
      name:
        metaName ||
        name ||
        sourceName ||
        flowService.getSourceName({
          selectedBucket,
          selectedTable,
          selectedCredential,
          connectionCategory,
        }),
      description: metaDescription || description || sourceDescription || '',
      flow_type: flowTypes.spark,
      source_config: sourceConfigWithMode,
    };
  }, [
    flowService,
    databaseService,
    source,
    selectedBucket,
    selectedTable,
    selectedCredential,
    connectionCategory,
    sourceConfig,
    sourceConfigFields,
    selectedConnection,
    currentOrg,
    metaName,
    metaDescription,
  ]);

  const buildSinkInfo = () => {
    let { name, description, sourceName, sourceDescription, ...sink_config } = sinkConfig;
    sink_config = withUiModeDefault(sink_config);
    const { sink_type = selectedConnection } = sink_config;

    let sinkInfo: any = {
      ...(sink?.id ? { id: sink.id } : {}),
      name:
        metaName ||
        name ||
        sourceName ||
        sink?.name ||
        flowService.getSinkName({
          selectedBucket,
          selectedTable,
          dataMapSettings: {},
          connectionCategory,
          selectedCredential,
          metaInfo,
          selectedConnection,
        }),
      flow_type: flowTypes.spark,
      description: metaDescription || description || sourceDescription || '',
      sink_type,
      sink_config,
    };

    if (isNewTable) {
      sinkInfo.create_destination = 'true';
    }

    if (!sink?.id) {
      sinkInfo.data_set_id = nexset?.id;
    }

    return sinkInfo;
  };

  const onSourceConfigChanged = useCallback(
    (newSourceConfig: Record<string, any>) => {
      const sourceConfigWithMode = withUiModeDefault(newSourceConfig);
      updateSourceConfig(sourceConfigWithMode);
    },
    [updateSourceConfig],
  );

  const onSinkConfigChanged = useCallback(
    (newSinkConfig: Record<string, any>, field: any, value: any) => {
      const sinkConfigWithMode = withUiModeDefault(newSinkConfig);
      updateSinkConfig(sinkConfigWithMode, field, value);
    },
    [updateSinkConfig],
  );

  const history = useHistory();

  const updateSource = useCallback(
    (runWithIngestion?: boolean) => {
      const sourceInfo = buildSourceInfo();
      // Update the form data with runWithIngestion before calling the API
      if (runWithIngestion !== undefined) {
        updateFormData({ runWithIngestion });
      }

      return requestSourceUpdate(sourceInfo, runWithIngestion);
    },
    [buildSourceInfo, requestSourceUpdate, updateFormData],
  );

  const createSource = useCallback(() => {
    const sourceInfo = buildSourceInfo();
    return requestSourceCreation(sourceInfo);
  }, [buildSourceInfo, requestSourceCreation]);

  const updateSink = useCallback(() => {
    const sinkInfo = buildSinkInfo();
    return requestSinkUpdate(sinkInfo);
  }, [requestSinkUpdate, buildSinkInfo]);

  const createSink = useCallback(() => {
    const sinkInfo = buildSinkInfo();
    return requestSinkCreation(sinkInfo);
  }, [requestSinkCreation, buildSinkInfo]);

  const navigateAfterSaveSink = async (newSink) => {
    if (!!newSink?.id) {
      if (isModal) {
        onDismiss?.({ addedDataSinkId: newSink.id, ...newSink });
        return;
      }
      if (id) {
        history.goBack();
      } else {
        try {
          const flowNodeId = newSink?.origin_node_id || newSink?.flow_node_id;
          setIsRedirecting(true);
          const assignedProjectId = await assignFlowToProjectIfPresent({
            dispatch,
            flowNodeId,
            currentProjectId,
          });
          if (assignedProjectId) {
            redirectToProjectIfNeeded(history, assignedProjectId, dispatch);
            return;
          }
        } catch (error) {
          console.error('Failed to assign flow to project:', error);
        } finally {
          setIsRedirecting(false);
        }
        history.replace(flowService.getPathToGoAfterCreation(previousPath));
      }
    }
  };

  const navigateAfterSaveSource = async (newSource) => {
    if (!!newSource?.id) {
      if (isModal) {
        onDismiss?.({ addedDataSourceId: newSource.id, ...newSource });
        return;
      }
      if (id) {
        // Editing existing source - just go back
        history.goBack();
      } else {
        // Creating new source - navigate to edit screen if it created a new flow
        try {
          const flowNodeId = newSource?.origin_node_id || newSource?.flow_node_id;
          setIsRedirecting(true);
          const assignedProjectId = await assignFlowToProjectIfPresent({
            dispatch,
            flowNodeId,
            currentProjectId,
          });
          if (assignedProjectId) {
            redirectToProjectIfNeeded(history, assignedProjectId, dispatch);
            return;
          }
        } catch (error) {
          console.error('Failed to assign flow to project:', error);
        } finally {
          setIsRedirecting(false);
        }
        const flowOriginId = newSource?.origin_node_id || null;
        history.replace(
          flowService.getPathToGoAfterCreation(previousPath, undefined, {
            resourceType: 'source',
            flowType: 'spark',
            flowOriginId,
          }),
        );
      }
    }
  };

  const requestSource = useCallback(
    async (runWithIngestion?: boolean) => {
      try {
        let newSource;
        if (source) {
          newSource = await updateSource(runWithIngestion);
        } else {
          newSource = await createSource();
        }
        navigateAfterSaveSource(newSource);
        cleanForm(newSource?.id);
        return !!newSource.id;
      } catch (e) {
        return false;
      }
    },
    [source, updateSource, createSource],
  );

  const requestSink = useCallback(async () => {
    try {
      let newSink;
      if (!!sink?.id) {
        newSink = await updateSink();
      } else {
        newSink = await createSink();
      }
      cleanForm(newSink?.id);
      navigateAfterSaveSink(newSink);
      return !!newSink?.id;
    } catch (e) {
      return false;
    }
  }, [sink, updateSink, createSink]);

  const onNext = useCallback(async () => {
    if (!validate(showError)) {
      return false;
    }

    // Check if we should show save confirmation
    const shouldShow = !!(
      source && // We have a source object
      source?.status === 'ACTIVE' && // Source is active
      shouldRenderRunAction(source, connectionCategory) &&
      currentHasChanges && // Only show confirmation if there are actual changes
      !isSaving
    );

    if (shouldShow) {
      setShowSaveConfirmation(true);
      // Store the save function to call later and return a promise that resolves when save completes
      return new Promise<boolean>((resolve) => {
        pendingSaveRef.current = async (runWithIngestion: boolean) => {
          let result;
          if (connectionType === 'SOURCE') {
            result = await requestSource(runWithIngestion);
          }
          if (result) {
            setShowLeaveConfirmation(false);
          }
          resolve(result);
          return result;
        };
      });
    }

    // No save confirmation needed, proceed with normal save
    let result;
    if (connectionType === 'SOURCE') {
      result = await requestSource();
    } else {
      result = await requestSink();
    }

    // If save was successful, complete the form flow to prevent exit modal
    if (result) {
      setShowLeaveConfirmation(false);
    }

    return result;
  }, [
    requestSink,
    requestSource,
    validate,
    currentHasChanges,
    source,
    connectionCategory,
    isSaving,
    currentRunWithIngestion,
    connectionType,
  ]);

  useEffect(() => {
    return () => {
      overrideFormNextFunction(null);
    };
  }, []);

  useEffect(() => {
    overrideFormNextFunction(onNext);

    // Cleanup: Reset override function when dependencies change
    return () => {
      overrideFormNextFunction(null);
    };
  }, [onNext, overrideFormNextFunction, currentHasChanges]);

  const nexsetSchema = getNexsetSchemaById(nexset?.id);

  const onSinkMappingChanged = (newSinkMapping, auto) => {
    onSinkMappingUpdated({
      connectionCategory,
      sinkConfig,
      isEditMode: !!sink?.id,
    })(newSinkMapping, auto);
  };

  /**
   * function responsible to create the resource on JSON Config Mode
   */
  const onSaveAdvancedMode = (data: any) => {
    const advancedConfig = withUiModeCode(data);

    if (connectionType === 'SOURCE') {
      updateSourceConfig(advancedConfig);
      return;
    }

    updateSinkConfig(advancedConfig, null, null);
  };

  return {
    initialLoading,
    isRedirecting,
    nexsetSchema,
    onSinkConfigChanged,
    onTableSelected,
    onBucketSelected,
    sinkConfigFields,
    onSinkMappingChanged,
    allowCustomPrefixEditor: service.canEditPrefix({
      connectionCategory,
      selectedBucket,
      sinkConfig,
    }),
    onSourceConfigChanged,
    onSaveAdvancedMode,
    // Save confirmation state and functions
    showSaveConfirmation,
    setShowSaveConfirmation,
    isSaving,
    setIsSaving,
    pendingSaveRef,
    currentRunWithIngestion,
    hasChanges,
    shouldShowSaveConfirmation,
    handleSaveConfirmation,
    onCloseSaveConfirmation,
    setShowLeaveConfirmation,
    overrideFormNextFunction,
  };
}
